import { createStore } from 'effector'
import {
  authUserFx,
  getExternalProfileFx,
  getFriendsListFx,
  getFullExternalProfileFx,
  getReferralHistoryFx,
  getSteamAuthUrlFx,
  getUsersByGamedIdFx,
  refreshTokenFx,
} from './effects'
import { persist } from 'effector-storage/local'
import { TGAccountDataType, UserInfo } from '@/entities/user'
import { ClaimFriendsRewardFx } from './effects'
import { getUserProfileFx } from './effects'
import { useSteamDataResponse } from './hooks'
import {
  changeLocalPrivacy,
  clearUsersByGamedId,
  deleteExternalProfile,
  setIsFirstLogin,
  updatePremiumStatus,
  updateUserLanguage,
} from './events'
import {
  Friend,
  ReferralInfoType,
  ScoringDiffType,
  UserSettingsType,
  PremiumLevel,
  ReferralItemType,
  UserByGamedId,
} from './types'
import { isBefore, parseISO } from 'date-fns'
import { $steamUserData } from '../platforms/steam'
import { Pagination } from '@/shared/constants/types'

export const $friendsList = createStore<Friend[]>([]).on(getFriendsListFx.doneData, (_, data) => {
  return data.data
})

const $isUserLoading = createStore<boolean>(false).on(authUserFx.pending, (_, pending) => pending)

const $steamId = createStore<number | null>(null)
  .on(authUserFx.doneData, (_, data) => (data.user.steam_id ? data.user.steam_id : null))
  .on(getUserProfileFx.doneData, (_, data) => {
    const newData = useSteamDataResponse(data)
    return newData.userData.steamId ? newData.userData.steamId : null
  })

persist({ store: $steamId, key: 'steamId' })

const $tgAccuntData = createStore<TGAccountDataType | null>(null).on(authUserFx.doneData, (_, data) => data)

export const $gamedId = createStore<string | null>(null).on($steamUserData.updates, (_, data) => {
  if (data?.userData.gamed_id) {
    return data.userData.gamed_id
  }
  return 'XXXXXX'
  // if (!data || !data.user) return null
  // const tid = data.user.tid.toString().padStart(6, '0')
  // const gid = `${tid.slice(0, 3)}-${tid.slice(3)}`
  // return gid
})

const $userToken = createStore<string | null>(null)
  .on(authUserFx.doneData, (_, data) => data.token)
  .on(refreshTokenFx.doneData, (_, data) => data)

const $isTokenLoading = createStore<boolean>(false)
  .on(authUserFx.pending, (_, pending) => pending)
  .on(refreshTokenFx.pending, (_, pending) => pending)

const $userSteamAuthUrl = createStore<string | null>(null).on(getSteamAuthUrlFx.doneData, (_, data) => {
  if (!data?.connect_url) return null
  return data.connect_url
})

persist({ store: $userSteamAuthUrl, key: 'userSteamAuthUrl' })

const $userBalance = createStore<number>(0)
  .on(getUserProfileFx.doneData, (_, data) => {
    const newData = useSteamDataResponse(data)
    return Math.ceil(newData.userData.tokenValue ? newData.userData.tokenValue : 0)
  })
  .on(authUserFx.doneData, (_, data) => {
    return Math.ceil(data.user.token_value)
  })

export const $farcanaGigiBalance = createStore<number>(0)
  .on(getUserProfileFx.doneData, (_, data) => {
    const newData = useSteamDataResponse(data)
    return Math.ceil(newData.userData.farcana_gigi ? newData.userData.farcana_gigi : 0)
  })
  .on(authUserFx.doneData, (_, data) => {
    return Math.ceil(data.user.farcana_gigi)
  })

export const $gigiBalance = createStore<number>(0)
  .on(getUserProfileFx.doneData, (_, data) => {
    const newData = useSteamDataResponse(data)
    return Math.ceil(newData.userData.gigi_index ? newData.userData.gigi_index : 0)
  })
  .on(authUserFx.doneData, (_, data) => {
    return Math.ceil(data.user.gigi_index)
  })

export const $unifiedGigi = createStore<number>(0)
  .on($gigiBalance.updates, (_, data) => {
    return data + $farcanaGigiBalance.getState()
  })
  .on($farcanaGigiBalance.updates, (_, data) => {
    return data + $gigiBalance.getState()
  })

export type LangType = 'en' | 'ru'
const $userLanguage = createStore<LangType>((localStorage.getItem('userLanguage') as LangType) || 'en').on(
  updateUserLanguage,
  (_, data) => (data !== 'ru' ? 'en' : 'ru')
)
persist({ store: $userLanguage, key: 'userLanguage' })

export const $isFirstSteamLogin = createStore<boolean>(false).on(setIsFirstLogin, (_, data) => data)

export const $isUpdateProgressAvailable = createStore<boolean>(false).on(getUserProfileFx.doneData, (_, data) => {
  const isAvailable: boolean = new Date(data.user.next_steam_update_unlocked) <= new Date()
  return isAvailable
})

export const $externalProfile = createStore<UserInfo | null>(null)
  .on(getFullExternalProfileFx.doneData, (_, data) => data)
  .on(deleteExternalProfile, () => null)

export const $xtGigi = createStore<number>(0).on(getExternalProfileFx.doneData, (_, data) => {
  return data.gigi
})

export const $userSettings = createStore<UserSettingsType>({})
  .on(getUserProfileFx.doneData, (_, data) => {
    if (!data.settings) {
      return {}
    }
    return data.settings
  })
  .on(getUserProfileFx.failData, () => {
    return {}
  })
  .on(changeLocalPrivacy, (state, isPrivate) => {
    return {
      ...state,
      is_private: isPrivate,
    }
  })

export const $userRefInfo = createStore<ReferralInfoType | null>(null).on(
  getUserProfileFx.doneData,
  (_, data) => data.referral_info
)

export const $totalReward = createStore<number>(0).on(getUserProfileFx.doneData, (_, data) => {
  if (data.steam_data) {
    const newData = data.steam_data.scoring_diff
    const totalReward = newData.games_owned + newData.total_hours + newData.inventory_price
    return totalReward
  }
  return 0
})

export const $referalReward = createStore<number>(0).on(getUserProfileFx.doneData, (_, data) => {
  useSteamDataResponse(data)
  const newData = useSteamDataResponse(data)
  return newData.referal_info.unclaimed
})

export const $referalRewardTotal = createStore<number>(0).on(getUserProfileFx.doneData, (_, data) => {
  const newData = useSteamDataResponse(data)
  return newData.referal_info.claimed_total
})

export const $invitedFriendsL1 = createStore<number>(0).on(getUserProfileFx.doneData, (_, data) => {
  const newData = useSteamDataResponse(data)
  return newData.referal_info.referred_level_1
})

export const $invitedFriendsL2 = createStore<number>(0).on(getUserProfileFx.doneData, (_, data) => {
  const newData = useSteamDataResponse(data)
  return newData.referal_info.referred_level_2
})

export const $nextClaimDate = createStore<string | null>(null)
  .on(getUserProfileFx.doneData, (_, data) => {
    const newData = useSteamDataResponse(data)
    return newData.referal_info.unclaimed_expire_at
  })
  .on(ClaimFriendsRewardFx.doneData, (_, data) => data.unclaimed_expire_at)

export const $updateDiff = createStore<ScoringDiffType | null>(null).on(getUserProfileFx.doneData, (_, data) => {
  const newData = useSteamDataResponse(data)
  return newData.scoring_diff
})

export const $referalLink = createStore<string | null>(null).on(getUserProfileFx.doneData, (_, data) => {
  const newData = useSteamDataResponse(data)
  return newData.userData.ref_url
})

export const $newItem = createStore<ScoringDiffType | null>(null).on(getUserProfileFx.doneData, (_, data) => {
  const newData = useSteamDataResponse(data)
  return newData.scoring_diff
})

export const $userOldBalance = createStore<number>(0).on(getUserProfileFx.doneData, (_, data) => {
  const newData = useSteamDataResponse(data)
  return Math.ceil(newData.userData.previous_token_value_total)
})

export const $premiumLevel = createStore<PremiumLevel>(PremiumLevel.UNSUBSCRIBED).on(
  getUserProfileFx.doneData,
  (_, data) => {
    const newData = useSteamDataResponse(data)
    if (newData.userData.gamed_premium_until) {
      const parsedDate = parseISO(newData.userData.gamed_premium_until)
      const hasPassed = isBefore(parsedDate, new Date())
      if (hasPassed) {
        return PremiumLevel.UNSUBSCRIBED
      }
      return newData.userData.level_premium ?? PremiumLevel.UNSUBSCRIBED
    }
    return PremiumLevel.UNSUBSCRIBED
  }
)

export const $isUserGamedPremium = createStore<boolean>(false)
  .on(getUserProfileFx.doneData, (_, data) => {
    const newData = useSteamDataResponse(data)
    if (!newData.userData.gamed_premium_until) {
      return false
    }
    const parsedDate = parseISO(newData.userData.gamed_premium_until)
    const hasPassed = isBefore(parsedDate, new Date())
    return !hasPassed
  })
  .on(updatePremiumStatus, (_, data) => {
    return !data
  })

export const $referralHistoryList = createStore<ReferralItemType[] | null>(null).on(
  getReferralHistoryFx.doneData,
  (state, data) => {
    const incoming = data.data
    const existing = state ?? []

    const existingIds = new Set(existing.map((item) => item.tid))

    const newItems = incoming.filter((item) => !existingIds.has(item.tid))

    return [...existing, ...newItems]
  }
)

export const $referralHistoryPagination = createStore<Pagination | null>(null).on(
  getReferralHistoryFx.doneData,
  (_, data) => data.pagination
)

export const $usersByGamedId = createStore<UserByGamedId[] | null>(null)
  .on(getUsersByGamedIdFx.doneData, (state, data) => {
    const incoming = data.data
    const existing = state ?? []

    const existingIds = new Set(existing.map((item) => item.friend_tid))

    const newItems = incoming.filter((item) => !existingIds.has(item.friend_tid))

    return [...existing, ...newItems]
  })
  .reset(clearUsersByGamedId)

export const $usersByGamedIdPagination = createStore<Pagination | null>(null).on(
  getUsersByGamedIdFx.doneData,
  (_, data) => data.pagination
)

export {
  $steamId,
  $isUserLoading,
  $tgAccuntData,
  $userBalance,
  $userToken,
  $userSteamAuthUrl,
  $userLanguage,
  $isTokenLoading,
}
